/**
 * progress-pill.tsx — where a campaign or a chapter stands for the student,
 * as one pill: its tone and its words from one table, so a status has both or
 * the build fails. The home's campaign cards, the campaign list and a
 * campaign's page draw a campaign's (campaignStatus), a campaign's chapters and
 * a chapter's page a chapter's (chapterStatus), and the portfolio a campaign's
 * by completion alone: unfinished work remains available after its live
 * sessions end. A session's status is its own pill
 * (student-session-card).
 */

import { useTranslation } from 'react-i18next';
import { Pill, type PillTone } from '../../components/pill';
import type { ProgressStatus } from '../../api/student/campaigns/gating';

/** What a progress pill labels: a campaign or a chapter, whose words agree with it where a language has gender. */
type Progressing = 'campaign' | 'chapter';

/**
 * Each status's tone, and its words for a campaign and for a chapter — keys
 * written whole. A pill names one thing, so its words are singular and agree
 * with it (es Completada for a campaign, Completado for a chapter); the
 * campaign list's filter tabs keep their plurals.
 */
const PROGRESS_LOOK: Record<ProgressStatus, { tone: PillTone; words: Record<Progressing, string> }> = {
  completed: { tone: 'success', words: { campaign: 'student.status.campaign.completed', chapter: 'student.status.chapter.completed' } },
  active: { tone: 'active', words: { campaign: 'student.status.campaign.active', chapter: 'student.status.chapter.active' } },
  ended: { tone: 'info', words: { campaign: 'student.status.campaign.ended', chapter: 'student.status.chapter.ended' } },
  upcoming: { tone: 'brand', words: { campaign: 'student.status.campaign.upcoming', chapter: 'student.status.chapter.upcoming' } },
  locked: { tone: 'locked', words: { campaign: 'student.status.campaign.locked', chapter: 'student.status.chapter.locked' } },
};

/** Where `of`, a campaign or a chapter, stands for the student. */
export function ProgressPill({ of, status }: { of: Progressing; status: ProgressStatus }) {
  const { t } = useTranslation();
  const look = PROGRESS_LOOK[status];
  return <Pill tone={look.tone}>{t(look.words[of])}</Pill>;
}
